import { zodResolver } from '@hookform/resolvers/zod';
import { t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { ClientInformationSchema } from '@repo/shared/schema';
import { ReportAutofillStatus } from '@repo/shared/types';
import { Button } from '@repo/ui/button';
import { DateInput } from '@repo/ui/date-input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { Separator } from '@repo/ui/separator';
import { TypewriterField } from '@repo/ui/typewriter-input';
import { Check, Loader2, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useParams, useRouteLoaderData } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { useONavigate } from '@/hooks/use-organization-navigate';
import type { WizardLoaderData, WizardReport } from '@/lib/report';
import { trpc } from '@/providers/trpc';
import { useSetUnsavedChanges } from '@/providers/unsaved-changes';

const ZClientInformationFormSchema = ClientInformationSchema.extend({ _id: z.string() });
type TClientInformationForm = z.infer<typeof ZClientInformationFormSchema>;

type SaveState = 'idle' | 'saving' | 'saved';

export function ClientInformationStep() {
  useLocale();

  const { reportId = '' } = useParams();
  const oNavigate = useONavigate();

  const { report: initialReport } = useRouteLoaderData('report-create-wizard') as WizardLoaderData;

  const { setUnsavedChanges, clearUnsavedChanges } = useSetUnsavedChanges();
  const hasMarkedUnsaved = useRef(false);

  const { data: report } = trpc.report.getReport.useQuery(
    { reportId },
    {
      initialData: initialReport as never,
      refetchInterval: (query) => {
        const data = query.state.data as WizardReport | undefined;
        return data?.autofillStatus === ReportAutofillStatus.PROCESSING ? 2500 : false;
      },
    },
  );

  const isAutofillRunning = report?.autofillStatus === ReportAutofillStatus.PROCESSING;

  const { mutateAsync: updateClientInfo } = trpc.report.updateClientInfo.useMutation({
    meta: { doNotInvalidateQueryOnMutation: true },
  });

  const { mutate: submitReport, isPending: isSubmitting } = trpc.report.submitReport.useMutation({
    onSuccess: () => {
      void clearUnsavedChanges();
      toast.success(t('reports.reportSubmittedSuccessfully'));
      void oNavigate({ to: `/reports` });
    },
    onError: () => {
      toast.error(t('reports.failedToSubmitReport'), {
        get description() {
          return t('reports.pleaseCheckTheFormAndRetry');
        },
      });
    },
  });

  const form = useForm<TClientInformationForm>({
    resolver: zodResolver(ZClientInformationFormSchema, { error: validationError }),
    defaultValues: {
      firstName: report?.clientInformation?.firstName,
      lastName: report?.clientInformation?.lastName,
      ...report?.clientInformation,
    },
  });

  const { isDirty } = form.formState;

  useEffect(() => {
    if (!isDirty || hasMarkedUnsaved.current) return;
    hasMarkedUnsaved.current = true;
    setUnsavedChanges({
      get message() {
        return t('reports.youHaveEditedClientInformationLeavingNowMayLose');
      },
      get proceedLinkLabel() {
        return t('extra.leaveAnyway');
      },
    });
  }, [isDirty, setUnsavedChanges]);

  // Reset form when autofill finishes and populates data
  useEffect(() => {
    if (report?.clientInformation && !isAutofillRunning) {
      form.reset({
        ...report.clientInformation,
      });
    }
    // Only reset on autofill completion
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutofillRunning]);

  // ── Autosave ──────────────────────────────────────────────────────────────
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);

  const watchedValues = useWatch({ control: form.control });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(async () => {
      setSaveState('saving');
      try {
        await updateClientInfo({
          reportId,
          clientInformation: watchedValues as Partial<TClientInformationForm>,
        });
        setSaveState('saved');
        setTimeout(() => setSaveState('idle'), 2000);
      } catch {
        setSaveState('idle');
      }
    }, 600);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValues]);

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-6"
        onSubmit={form.handleSubmit(() => submitReport({ reportId }))}
      >
        {/* Save indicator */}
        <div className="flex h-5 items-center justify-end">
          {saveState === 'saving' && (
            <span className="flex items-center gap-1.5 text-muted-foreground text-xs">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('reports.saving')}{' '}
            </span>
          )}
          {saveState === 'saved' && (
            <span className="flex items-center gap-1.5 text-green-600 text-xs">
              <Check className="h-3.5 w-3.5" /> {t('reports.saved')}{' '}
            </span>
          )}
        </div>

        {/* ── Identity ── */}
        <Section title={t('reports.identity')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField control={form.control} label={t('reports.firstName2')} name="firstName" />
            <TextField control={form.control} label={t('reports.lastName2')} name="lastName" />
            <TextField
              control={form.control}
              label={t('reports.dateOfBirth2')}
              name="dateOfBirth"
              placeholder={t('reports.yyyyMmDd')}
            />
            <TextField
              control={form.control}
              label={t('reports.placeOfBirth2')}
              name="placeOfBirth"
            />
            <TextField control={form.control} label={t('reports.nationality')} name="nationality" />
            <TextField
              control={form.control}
              label={t('reports.countryOfResidence')}
              name="residenceCountry"
            />
          </div>
        </Section>

        <Separator />

        {/* ── Document ── */}
        <Section title={t('reports.identificationDocument')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              control={form.control}
              label={t('reports.idPassportNumber')}
              name="idNumber"
            />
            <TextField
              control={form.control}
              label={t('reports.taxIdentificationNumber2')}
              name="taxIdentificationNumber"
            />
          </div>
        </Section>

        <Separator />

        {/* ── Contact ── */}
        <Section title={t('reports.contact')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField control={form.control} label={t('reports.phone')} name="phone" type="tel" />
            <TextField
              control={form.control}
              label={t('reports.email')}
              name="email"
              type="email"
            />
            <div className="sm:col-span-2">
              <TextField control={form.control} label={t('reports.address')} name="address" />
            </div>
          </div>
        </Section>

        <Separator />

        {/* ── Financial ── */}
        <Section title={t('reports.financialProfile')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              control={form.control}
              label={t('reports.employmentStatus2')}
              name="employmentStatus"
            />
            <TextField
              control={form.control}
              label={t('reports.sourceOfFunds2')}
              name="sourceOfFunds"
            />
            <TextField
              control={form.control}
              label={t('reports.accountUsePurpose')}
              name="accountUse"
            />
            <TextField
              control={form.control}
              label={t('reports.estimatedNetWorth')}
              name="netWorth"
            />
          </div>
        </Section>

        <div className="flex justify-end pt-2">
          <Button disabled={isSubmitting} loading={isSubmitting} type="submit">
            <Send className="me-2 h-4 w-4" />
            {t('reports.submitReport')}{' '}
          </Button>
        </div>
      </form>
    </Form>
  );
}

// ── Small reusable helpers ────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  useLocale();

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-foreground text-sm">{title}</h3>
      {children}
    </div>
  );
}

function TextField({
  control,
  name,
  label,
  placeholder,
  type = 'text',
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  name: string;
  label: string;
  placeholder?: string;
  type?: string;
}) {
  useLocale();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            {name === 'dateOfBirth' || name === 'incorporationDate' ? (
              <DateInput {...field} placeholder={label} />
            ) : (
              <TypewriterField placeholder={placeholder ?? label} type={type} {...field} />
            )}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
